import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Card, Spin, Empty, Typography, Tag, Tooltip, Space, Button, message, Dropdown,
} from 'antd';
import { BankOutlined, ReloadOutlined, CopyOutlined, ClockCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getAccountsApi } from '../../api/accounts';
import { decryptData } from '../../utils/crypto';
import { formatDate, escapeHtml } from '../../utils/formatters';

const { Title, Text } = Typography;

export default function BankAccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await getAccountsApi({ type: 'bank' });
      const all  = res.data?.data || res.data || [];
      // Already ordered by last income date (newest first) server-side.
      const banks = all
        .filter((a) => String(a.type).toLowerCase() === 'bank')
        .map((a) => {
          const dec = a.bank_details ? decryptData(a.bank_details) || {} : {};
          return { ...a, ...dec };
        });
      setAccounts(banks);
    } catch {
      message.error('Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => message.success('Copied!'));
  };

  const maskAccountNumber = (v) => v
    ? `${'•'.repeat(Math.max(0, String(v).length - 4))}${String(v).slice(-4)}`
    : '—';

  const handlePrint = (orientation = 'portrait') => {
    const rows = accounts.map((a, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${escapeHtml(a.bank_name || '—')}</td>
        <td>${escapeHtml(a.account_holder_name || '—')}</td>
        <td>${escapeHtml(maskAccountNumber(a.account_number))}</td>
        <td>${escapeHtml(a.ifsc_code || '—')}</td>
        <td>${a.last_income_date ? formatDate(a.last_income_date) : 'No income yet'}</td>
      </tr>`).join('');

    let userName = '';
    try { userName = JSON.parse(localStorage.getItem('user'))?.name || ''; } catch { }
    const logoUrl = `${window.location.origin}/logo.png`;

    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head>
      <title>Bank Accounts Report — Kheti Hisab</title>
      <style>
        @page { size: A4 ${orientation}; margin: 12mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: Arial, sans-serif; font-size: 13px; padding: 28px 32px; color: #111; }
        .brand { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
        .brand img { width: 40px; height: 40px; object-fit: contain; }
        .brand-name { font-size: 22px; font-weight: 700; color: #1a6b2a; letter-spacing: -0.3px; }
        .brand-tagline { font-size: 11px; color: #6b7280; margin-top: 1px; }
        .divider { border: none; border-top: 2px solid #1a6b2a; margin: 12px 0 16px; }
        .report-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 16px; }
        .report-title { font-size: 16px; font-weight: 700; color: #111; }
        .report-meta { font-size: 11px; color: #6b7280; text-align: right; line-height: 1.6; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #1a6b2a; color: #fff; padding: 8px 10px; text-align: left; font-size: 12px; }
        td { padding: 7px 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
        tbody tr:nth-child(odd) td { background: #ffffff; }
        tbody tr:nth-child(even) td { background: #eef5ec; }
        .footer { margin-top: 28px; text-align: center; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 10px; }
        @media print { button { display: none; } }
      </style>
    </head><body>
      <div class="brand">
        <img src="${logoUrl}" alt="Kheti Hisab" onerror="this.style.display='none'" />
        <div>
          <div class="brand-name">Kheti Hisab</div>
          <div class="brand-tagline">Smart Farm Management</div>
        </div>
      </div>
      <hr class="divider" />
      <div class="report-header">
        <div class="report-title">Bank Accounts Report</div>
        <div class="report-meta">
          ${userName ? `<div>Account: <strong>${escapeHtml(userName)}</strong></div>` : ''}
          <div>Printed: ${dayjs().format('DD MMM YYYY, hh:mm A')}</div>
          <div>Entries: ${accounts.length}</div>
        </div>
      </div>
      <table>
        <thead><tr><th style="width:36px">Sr No.</th><th>Bank Name</th><th>Account Holder</th><th>Account Number</th><th>IFSC Code</th><th>Last Income</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">khethisab.shop &nbsp;·&nbsp; Kheti Hisab — Smart Farm Management</div>
      <script>window.onload = () => { window.print(); }<\/script>
    </body></html>`);
    win.document.close();
  };

  const columns = [
    {
      title: 'Bank Name',
      dataIndex: 'bank_name',
      render: (v) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: '#e6fffb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <BankOutlined style={{ fontSize: 17, color: '#13c2c2' }} />
          </div>
          {v
            ? <Tag color="cyan" style={{ fontWeight: 500 }}>{v}</Tag>
            : <Text type="secondary">—</Text>
          }
        </div>
      ),
    },
    {
      title: 'Account Holder',
      dataIndex: 'account_holder_name',
      render: (v) => <Text>{v || '—'}</Text>,
    },
    {
      title: 'Account Number',
      dataIndex: 'account_number',
      render: (v) => v ? (
        <Space size={4}>
          <Text style={{ fontFamily: 'monospace', letterSpacing: 1 }}>
            {'•'.repeat(Math.max(0, String(v).length - 4))}{String(v).slice(-4)}
          </Text>
          <Tooltip title="Copy">
            <Button
              type="text" size="small" icon={<CopyOutlined />}
              onClick={() => copy(v)}
              style={{ color: '#8c8c8c' }}
            />
          </Tooltip>
        </Space>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'IFSC Code',
      dataIndex: 'ifsc_code',
      render: (v) => v ? (
        <Space size={4}>
          <Text style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1a6b2a' }}>{v}</Text>
          <Tooltip title="Copy">
            <Button
              type="text" size="small" icon={<CopyOutlined />}
              onClick={() => copy(v)}
              style={{ color: '#8c8c8c' }}
            />
          </Tooltip>
        </Space>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Last Income',
      dataIndex: 'last_income_date',
      render: (v) => v ? (
        <Tag icon={<ClockCircleOutlined />} color="green">{formatDate(v)}</Tag>
      ) : <Text type="secondary">No income yet</Text>,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0, color: '#1a6b2a' }}>
          <BankOutlined style={{ marginRight: 8 }} />
          Bank Accounts
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchAccounts} loading={loading} />
          <Dropdown
            disabled={accounts.length === 0}
            menu={{
              items: [
                { key: 'portrait', label: 'Print — Portrait' },
                { key: 'landscape', label: 'Print — Landscape' },
              ],
              onClick: ({ key }) => handlePrint(key),
            }}
          >
            <Tooltip title="Print bank accounts list">
              <Button icon={<PrinterOutlined />} disabled={accounts.length === 0} />
            </Tooltip>
          </Dropdown>
        </Space>
      </div>

      <Card style={{ borderRadius: 10 }}>
        {loading && accounts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : accounts.length === 0 && !loading ? (
          <Empty description="No bank accounts found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            dataSource={accounts}
            columns={columns}
            rowKey="id"
            loading={loading}
            scroll={{ x: 700 }}
            pagination={false}
            onRow={(record) => ({
              onClick: () => navigate(`/accounts/${record.id}`, { state: { type: 'bank', name: record.name, from: '/bank-accounts' } }),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Card>
    </div>
  );
}
